import { Button } from '@progress/kendo-react-buttons';
import { Loader } from '@progress/kendo-react-indicators';
import { isEmpty } from 'lodash';
import cloneDeep from 'lodash.clonedeep';
import React, {
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
  JSX
} from 'react';
import { Container } from 'react-bootstrap';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { useToggle } from 'usehooks-ts';
import { Gallery } from '../../components/gallery';
import { Answer } from '../../components/questionnaire';
import {
  AchievementContent,
  AchievementContext
} from '../../contexts/achievement';
import { GameDocumentContext } from '../../contexts/game-document';
import {
  NotificationContent,
  NotificationContext
} from '../../contexts/notification';
import { PlayerContext, PlayerContextState } from '../../contexts/player';
import { addManualScoringAsync } from '../../services/players';
import {
  TaskContentAnswerEntity,
  TaskEntity,
  TaskEntity as TaskEntityPlayer
} from '../../types/entities';
import {
  ContentAnswer,
  ScoringReductionEntity
} from '../../types/entities/task';
import { TaskContentEntity } from '../../types/game-document/entities';
import {
  AnswerType,
  CheckboxOption,
  TaskContentAnswer,
  TaskContentForm
} from '../../types/game-document/entities/task-content';
import {
  GetTaskById,
  GetTaskById as GetTaskGameById
} from '../../utils/game-document/assets';
import {
  GetCustomFeedback,
  GetFormMedia,
  GetTaskContentById,
  isContainPremessageOrIntro
} from '../../utils/game-document/assets/task-contents';
import { GetResourceValue } from '../../utils/game-document/resources';
import {
  AddTask,
  GetTaskById as GetPlayerTaskById,
  CalculateTotal,
  UpdateTaskAsync
} from '../../utils/game-engine/assets/tasks';
import { UpdatePlayerStateAsync } from '../../utils/game-engine/base';
import {
  RemoveTimer,
  setPlayerScoreReduction
} from '../../utils/game-engine/player';
import { processMediaTags } from '../../utils/game-engine/media-tag-converter';
import { generateTitleById } from '../../utils/game-document/display-languages';
import { DisplayLanguageContext } from '../../contexts/display-languages';
import { useGameTaskPosition } from '../../hooks/use-game-task-position';
import { ConfirmationDialog } from '../../components/dialog/confirmation-dialog';
import { GameContext } from '../../contexts/game';
import RichTextContent from '../../components/richtext-content';
import { detectDeviceType } from '../../utils/device-type';
import { ArMiniGames, getOtherBaseUrl } from '../../constants/argame';
import { TFileUpload, useUploadChunk } from '../../hooks/use-upload-chunk';
import { useGameEvent } from '../../hooks/use-game-event';
import { getFromStore, putToStore } from '../../utils/indexedDB';
import { TScoring } from '../../hooks/use-scoring-on-background';
import { Resource } from '../../types/game-document';
import { TimerProgressForm } from '../../components/timer/progress-form';
import { useTimerActions } from '../../hooks/use-game-timer';

type TaskContentProps = {
  id: string;
  mapType: 'openStreetMap' | 'satelliteMap' | 'illustrated' | 'googleMaps';
  changeTaskIconToComplete: (taskId: string) => void;
  changeTaskIconToInComplete: (taskId: string) => void;
  className?: string;
  answerType?: AnswerType;
  isAccesible?: boolean;
};

export const defaultImage = {
  defaultFeedbackAllCorrectIconResId:
    'https://stsharedappstorage.blob.core.windows.net/prod-catalyst-files/checked.png',
  defaultFeedbackPartialCorrectIconResId:
    'https://stsharedappstorage.blob.core.windows.net/prod-catalyst-files/checked.png',
  defaultFeedbackAllWrongIconResId:
    'https://stsharedappstorage.blob.core.windows.net/prod-catalyst-files/cross.png'
};

export const defaultFeedbackMessage = {
  defaultAllCorrectFeedbackMessageResId: 'Congratulations, you got it!',
  defaultAllWrongFeedbackMessageResId: "That's not the answer!",
  defaultPartialCorrectFeedbackMessageResId:
    'Well done, you are partially correct.'
};

export const TaskContent = ({ ...props }: TaskContentProps) => {
  const [game, , , isOnline] = useContext(GameContext);
  const [player, setPlayer] = useContext(PlayerContext);
  const [gameDocument] = useContext(GameDocumentContext);
  const [toast, setToast] = useContext(AchievementContext);
  const [displayLanguageContext] = useContext(DisplayLanguageContext);
  const [notification, setNotification] = useContext(NotificationContext);

  const [question, setQuestion] = useState<string>('');
  const [options, setOptions] = useState<string[]>([]);
  const [answerType, setAnswerType] = useState<AnswerType>();
  const [taskQuestion, setTaskQuestion] = useState<{
    content: string;
    embeddedBlobs: Resource['embeddedBlobs'];
  }>();
  const [preMessageContent, setPreMessageContent] = useState<{
    content: string;
    embeddedBlobs: Resource['embeddedBlobs'];
  }>();
  const [data, setData] = useState<TaskContentEntity>({} as TaskContentEntity);
  const [formIndex, setFormIndex] = useState<number>(-1);
  const [contentAnswer, setContentAnswer] = useState<ContentAnswer[]>([]);
  const [checkboxOption, setCheckboxOption] = useState<CheckboxOption[]>([]);
  const [isLoadingContent, setIsLoadingContent] = useState<boolean>(false);
  const [isPreMessage, setIsPreMessage] = useState<boolean>(false);
  const [isTaskIntro, setIsTaskIntro] = useState<boolean>(false);
  const [totalTryAnswer, setTotalTryAnswer] = useState<number>(0);
  const [title, setTitle] = useState<string>('');
  const [questionScoringReduction, setQuestionScoringReduction] =
    useState<ScoringReductionEntity>();
  const [showNoAsnwerWarning, setShowNoAsnwerWarning] =
    useState<boolean>(false);
  const [selectedTab, setSelectedTab] =
    React.useState<string>('Task information');
  const [showARWarning, setShowARWarning] = useToggle();

  const { stop: stopTimer } = useTimerActions();

  const {
    isUploading,
    uploadStatus,
    uploadErrors,
    handleFileChange,
    retryUpload,
    cancelUpload
  } = useUploadChunk({
    onSuccessCallback: (res) => {
      updateAnswerValue(res.url, res.contextSize);
    }
  });
  const deviceType = detectDeviceType();

  const { removeTaskOverlay, removeTaskDistance } = useGameTaskPosition({
    mapType: props.mapType
  });
  const { executeTaskEvent } = useGameEvent();

  const hasMounted = useRef(false);
  const hasExecutedOnOpen = useRef(false);

  const qrScanned =
    player.playerState &&
    (player.playerState.openedTask ?? []).length > 1 &&
    (player.playerState.openedTask ?? [])[1] === 'qr-scanned';

  const getTaskContent = async (id: string) => {
    hasExecutedOnOpen.current = false;
    const gameDocTask = GetTaskGameById(gameDocument.gameDocument, id);

    //Get taskContent from gameDocument
    const taskContent = gameDocument?.gameDocument?.assets?.taskContents?.find(
      (x) => x.id === gameDocTask!.taskContentId
    );

    setTitle(getTitle(gameDocTask!, taskContent));

    let isExistIntroContent = false;
    let isExistPreMessageContent = false;

    if (taskContent !== undefined) {
      setData(taskContent);
      checkTaskPlayerStateIsExist(); //id: Task Id

      if (formIndex < 0) {
        const {
          premessage,
          premessageContent,
          premessageBlobs,
          intro,
          introContent,
          introBlobs
        } = isContainPremessageOrIntro(
          gameDocument.gameDocument!,
          taskContent.id,
          player?.playerState?.language?.name ?? ''
        );

        //disabled premessage if QR scanned successfully
        //player will straight go to intro and/or form question
        isExistPreMessageContent = qrScanned ? false : premessage;
        setIsPreMessage(qrScanned ? false : premessage);

        if (!qrScanned) {
          setIsLoadingContent(true);
          if (premessage) {
            if (player.playerState?.isPreloadCompleted) {
              setPreMessageContent({
                content: premessageContent,
                embeddedBlobs: premessageBlobs
              });
            } else {
              const result = await processMediaTags(premessageContent);
              setPreMessageContent({ content: result, embeddedBlobs: [] });
            }
          } else if (!premessage && gameDocTask?.enableQRCode) {
            isExistPreMessageContent = true;
            setIsPreMessage(true);
            setPreMessageContent({
              content:
                '<p style="text-align:center;"><b>QR Task</b>. Find and scan the QR code to open.</p>',
              embeddedBlobs: []
            });
          }
        }

        isExistIntroContent = intro;
        setIsTaskIntro(intro);

        if (intro) {
          if (player.playerState?.isPreloadCompleted) {
            setTaskQuestion({
              content: introContent,
              embeddedBlobs: introBlobs
            });
          } else {
            const result = await processMediaTags(introContent);
            setTaskQuestion({ content: result, embeddedBlobs: [] });
          }
        }
        setIsLoadingContent(false);

        // Fire onOpenTask when intro popup loads (without premessage)
        if (!isExistPreMessageContent && isExistIntroContent) {
          if (!hasExecutedOnOpen.current) {
            hasExecutedOnOpen.current = true;
            const updatedPlayer = await executeTaskEvent(props.id, [
              'onOpenTask'
            ]);
            setPlayer(updatedPlayer);
          }
        }
      }
    }

    //#6323 #6199 skip callout form and intro form when content is empty
    //#6541 remove callout and change it into premessage
    if (!isExistPreMessageContent && !isExistIntroContent) {
      if (!hasExecutedOnOpen.current) {
        hasExecutedOnOpen.current = true;
        const updatedPlayer = await executeTaskEvent(props.id, ['onOpenTask']);
        setPlayer(updatedPlayer);
      }
      setFormIndex(0);
      setSelectedTab('Questions');

      if (taskContent?.ARMiniGame) {
        if (deviceType === 'iOS') {
          setPlayer((prev) => {
            if (!prev.playerState) return prev;
            if (prev.playerState.openedTask) prev.playerState.openedTask = [];
            return UpdatePlayerStateAsync(prev, prev.playerState);
          });
          window.location.href = generateARMiniGameLink;
        } else {
          setShowARWarning();
          return;
        }
      } else if (
        (!taskContent?.forms || taskContent?.forms.length === 0) &&
        formIndex < 0
      ) {
        updateTaskStatusToCompleted(player, true);
      }
    }
  };

  const isQuestionWithTimer = React.useMemo(() => {
    // -1 is still on premessage and intro
    if (formIndex < 0) return false;
    if (!data.forms) return false;
    return data.forms[formIndex].isScoringReduction ?? false;
  }, [data, formIndex]);

  const getTitle = (task: TaskEntity, content?: TaskContentEntity) => {
    if (content) {
      const contentTitle = GetResourceValue(
        gameDocument?.gameDocument!,
        content?.titleResId!,
        player?.playerState?.language?.name ?? ''
      );
      if (contentTitle !== undefined && contentTitle !== '')
        return contentTitle;
    }
    return getTaskTitle(task);
  };

  const getTaskTitle = (task: TaskEntity) =>
    GetResourceValue(
      gameDocument?.gameDocument!,
      task?.titleResId!,
      player?.playerState?.language?.name ?? ''
    );

  const getQuestion = () => {
    if (formIndex > -1 && data.forms && data.forms[formIndex]) {
      const questionResId = data.forms![formIndex].questionResId;
      const isMediaOnlyQuestion =
        data.forms![formIndex].mediaOnlyIdentifierResId;
      if (questionResId && !isMediaOnlyQuestion) {
        const question = GetResourceValue(
          gameDocument?.gameDocument!,
          questionResId,
          player?.playerState?.language?.name ?? ''
        );
        if (question !== undefined) {
          setQuestion(question);
        }
      }
    }
  };

  const getType = () => {
    if (data.forms && formIndex > -1) {
      const form = cloneDeep(data.forms[formIndex]);
      if (form) {
        setAnswerType(form.type);
      }
    }
  };

  const setAnswer = (formId: string) => {
    setContentAnswer([]);
    const task = GetPlayerTaskById(player, props.id);
    if (task) {
      const answer = task?.taskContentFormAnswers?.find(
        (x) => x?.formId === formId
      );

      if (answer && answer.answers) {
        setContentAnswer(cloneDeep(answer.answers));
      }
    }
  };

  const getOptions = (options: TaskContentAnswer[]) => {
    let optionsDescription: string[] = [];

    options?.forEach((opt) => {
      const description = getResourceById(opt.answerResId);
      if (description !== undefined) {
        optionsDescription.push(description);
      }
    });

    setOptions(cloneDeep(optionsDescription));
  };

  const getResourceById = (id: string) => {
    return GetResourceValue(
      gameDocument?.gameDocument!,
      id,
      player?.playerState?.language?.name!
    );
  };

  const updateTaskStatusToCompleted = async (
    player: PlayerContextState,
    isEmptyTaskContent: boolean = false,
    executeEvent: boolean = true
  ) => {
    let updatedPlayer = cloneDeep(player);
    const task = GetPlayerTaskById(updatedPlayer, props.id);

    if (task) {
      task.status = 'completed';

      // hide task if hideWhenComplete true
      const taskGame = GetTaskGameById(gameDocument.gameDocument, props.id);
      if (taskGame && taskGame.hideWhenComplete) {
        removeTaskOverlay(props.id);
      } else {
        props.changeTaskIconToComplete(props.id);
      }

      if (taskGame && taskGame.timerAssId) stopTimer(taskGame.timerAssId);

      const response = UpdateTaskAsync(updatedPlayer, props.id, task);
      if (response.openedTask!.length > 0) response.openedTask = [];
      response.latestCompletedTaskId = props.id;

      updatedPlayer = UpdatePlayerStateAsync(updatedPlayer, response);
    }

    if (executeEvent) {
      // execute all related task event
      updatedPlayer = await executeTaskEvent(
        props.id,
        isEmptyTaskContent
          ? ['onOpenTask', 'onCompleteTask', 'onCloseTask']
          : ['onCompleteTask', 'onCloseTask'],
        updatedPlayer
      );
    }

    setPlayer(updatedPlayer);
  };

  const generateARMiniGameLink = useMemo(() => {
    if (!data.ARMiniGame) return '';
    const selectedTask = GetTaskById(gameDocument.gameDocument, props.id);
    const ARGames = ArMiniGames[data.ARMiniGame];
    let timer = ARGames.defaultTimer;
    const baseUrl = getOtherBaseUrl();

    if (selectedTask && selectedTask.taskContentId) {
      const selectedTaskContent = GetTaskContentById(
        gameDocument.gameDocument,
        selectedTask.taskContentId
      );

      if (selectedTaskContent && selectedTaskContent.ARMiniGameSetting) {
        // implement timer from AR Game Setting
        timer =
          selectedTaskContent.ARMiniGameSetting.scoringReduction?.maximumTimeLimit?.toString() ??
          ARGames.defaultTimer;
      }
    }

    let modeParam = '';
    if (data.ARMiniGame === 'Where in the World?') {
      modeParam = '&mode=0';
    } else if (
      data.ARMiniGame === 'Where in the World? - Easy' ||
      data.ARMiniGame === 'Where in the World? - Hard'
    ) {
      const mode = data.ARMiniGameSetting?.mode ?? 0;
      modeParam = `&mode=${mode}`;
    }

    const url = `${baseUrl}/mini-games/?type=${ARGames.type}&gameCode=${game.gameCode}&playerCode=${player.playerState?.code ?? ''}&taskId=${props.id}&teamName=${player.playerState?.name}&timer=${timer}${modeParam}&env=${((window?._env_?.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_BASE_URL) as string).replaceAll('https://', '')}`;

    return url;
  }, [
    props.id,
    game.gameCode,
    data.ARMiniGame,
    data.ARMiniGameSetting,
    player.playerState?.code,
    player.playerState?.name,
    gameDocument.gameDocument
  ]);

  const nextQuestion = async () => {
    const closeTask = () => {
      setPlayer((prev) => {
        if (!prev.playerState) return prev;
        if (prev.playerState.openedTask) prev.playerState.openedTask = [];
        return UpdatePlayerStateAsync(prev, prev.playerState);
      });
    };

    let newIndex: number = isPreMessage ? -1 : formIndex + 1;
    setSelectedTab(newIndex < 0 ? 'Task information' : 'Questions');

    //Task has no question then complete & close the task
    // If onOpenTask hasn't been executed yet (e.g. no premessage scenario),
    // pass true so onOpenTask is included in the event execution.
    if ((!data.forms || data.forms?.length === 0) && !data.ARMiniGame) {
      updateTaskStatusToCompleted(player, !hasExecutedOnOpen.current);
      hasExecutedOnOpen.current = true;
    } else if (data.forms && newIndex > data?.forms?.length) {
      closeTask();
    }

    if (
      data &&
      data.forms &&
      data?.forms?.length > 0 &&
      (selectedTab === 'Questions' || formIndex === -1)
    )
      setFormIndex(newIndex);

    if (data.ARMiniGame) {
      if (deviceType === 'iOS') {
        closeTask();
        window.location.href = generateARMiniGameLink;
      } else {
        setShowARWarning();
        return;
      }
    } else {
      setIsPreMessage(false);
    }
  };

  const onLaunchHandler = async () => {
    let newIndex: number = isTaskIntro ? -1 : formIndex + 1;
    const closeTask = () => {
      setPlayer((prev) => {
        if (!prev.playerState) return prev;
        if (prev.playerState.openedTask) prev.playerState.openedTask = [];
        return UpdatePlayerStateAsync(prev, prev.playerState);
      });
    };

    setSelectedTab(newIndex < 0 ? 'Task information' : 'Questions');

    if (
      !isTaskIntro &&
      (!data.forms || data.forms?.length === 0) &&
      !data.ARMiniGame
    ) {
      updateTaskStatusToCompleted(player, false, false);
      return;
    } else if (newIndex === data?.forms?.length) {
      closeTask();
      return;
    }

    if (data && data.forms && data?.forms?.length > 0) {
      setFormIndex(newIndex);
    }

    if (!isTaskIntro && data.ARMiniGame) {
      if (deviceType === 'iOS') {
        closeTask();
        window.location.href = generateARMiniGameLink;
      } else {
        setShowARWarning();
        return;
      }
    } else {
      setIsPreMessage(false);
      setIsTaskIntro(isTaskIntro);
      if (!hasExecutedOnOpen.current) {
        hasExecutedOnOpen.current = true;
        const updatedPlayer = await executeTaskEvent(props.id, ['onOpenTask']);
        setPlayer(updatedPlayer);
      }
    }
  };

  const onCloseTaskContentHandler = async (executeEvent: boolean = true) => {
    const task = gameDocument.gameDocument?.assets?.tasks?.find(
      (x) => x.id === props.id
    );
    if (task) {
      let updatedPlayer = cloneDeep(player);

      if (executeEvent)
        updatedPlayer = await executeTaskEvent(task.id, ['onCloseTask']);

      if (
        updatedPlayer.playerState?.openedTask &&
        updatedPlayer.playerState?.openedTask.length > 0
      )
        updatedPlayer.playerState.openedTask = [];

      if (task.timerAssId)
        RemoveTimer(updatedPlayer.playerState!, task.timerAssId);

      setPlayer((prev) =>
        UpdatePlayerStateAsync(prev, updatedPlayer.playerState!)
      );
    }
  };

  const addManualScoring = async () => {
    const content: NotificationContent[] = notification.content;

    const form = data!.forms![formIndex];

    const answerTask = player?.playerState?.tasks?.find(
      (x) => x.id === props.id
    );

    const answer = answerTask?.taskContentFormAnswers?.find(
      (x) => x?.formId === form?.id
    );
    if (form) {
      // set message & iconType toast notification if question type manual scoring
      const manualScoringNotification: NotificationContent = {
        icon: 'notifications',
        isHide: false,
        message:
          generateTitleById(
            '32681cc9-9ee0-4d19-81fa-7a2b6e69a362',
            gameDocument,
            displayLanguageContext.displayLanguageSelected.resources!,
            'game'
          ) || 'Your answer has been submitted to the Facilitator for review'
      };
      content.push(manualScoringNotification);

      setNotification({
        ...notification,
        content
      });

      const isFileType = ['video', 'image', 'file'].includes(answerType ?? '');

      if (isOnline) {
        await addManualScoringAsync(
          gameDocument?.gameCode,
          player?.playerState?.code,
          props.id,
          form?.id,
          JSON.stringify(answer)
        );
      } else if (
        !isOnline &&
        (!isFileType || answer?.answers?.[0]?.value !== '')
      ) {
        const scoringQueue = await getFromStore<TScoring>(
          'scoring-queue',
          player.playerState?.code ?? ''
        );
        if (scoringQueue) {
          const selectedScoring = scoringQueue.scorings.find(
            (s) => s.taskId === props.id && s.formId === form.id
          );
          if (!selectedScoring) {
            const scorings = cloneDeep(scoringQueue.scorings);
            scorings.push({
              taskId: props.id,
              formId: form.id,
              answer: JSON.stringify(answer)
            });
            await putToStore('scoring-queue', {
              playerCode: player.playerState?.code ?? '',
              scorings
            });
          }
        }
      }
    }
  };

  const onSubmitHandler = async () => {
    const formId = data.forms![formIndex].id;

    if (!data.forms) return;

    const newPlayerState = await updateAnswerToPlayerState();
    const isManualScorring = data.forms[formIndex].isManualScoring;

    if (newPlayerState) {
      setPlayer((prev) => UpdatePlayerStateAsync(prev, newPlayerState));
      removeTaskDistance();
      const isCorrectAnswer: boolean = await checkCorrectAnswer();
      const newTask = GetPlayerTaskById(player, props.id);
      const newForm = newTask!.taskContentFormAnswers?.find(
        (x) => x?.formId === formId
      );
      const totalScoreReduction =
        newForm?.scoringReductions?.totalScoreReduction ?? 0;
      const totalOriginalScore = getPlayerScore();
      const totalAwaredScore = totalOriginalScore - totalScoreReduction;
      const awardedScore =
        totalAwaredScore < 1 &&
        answerType !== 'checkbox' &&
        answerType !== 'radio'
          ? 0
          : Math.round(totalAwaredScore);

      if (isManualScorring) addManualScoring();

      // #4505, update status to complete if player already answered all questions regardless correct or incorrect status
      const playerTasks = GetPlayerTaskById(player, props.id);
      const customFeedback = GetCustomFeedback(
        gameDocument.gameDocument,
        data.id,
        formIndex
      );
      // #4867 Multiple attempt case :
      // 1. If wrong answer and has no attempt = set task as completed
      // 2. If correct answer and has attempt = set task as completed
      // 3. If wrong answer and has attempt = task not set as competed
      if (
        data?.forms?.length === playerTasks?.taskContentFormAnswers?.length &&
        playerTasks?.taskContentFormAnswers?.findIndex(
          (x) => x.isCorrectAnswer === undefined
        ) === -1 &&
        playerTasks.status !== 'completed' &&
        playerTasks &&
        (playerTasks.taskContentFormAnswers![formIndex].isCorrectAnswer ||
          (!playerTasks.taskContentFormAnswers![formIndex].isCorrectAnswer &&
            playerTasks.taskContentFormAnswers![formIndex].attemptsLeft! <= 0))
      ) {
        if (!isManualScorring) {
          if (answerType === 'checkbox') {
            getCheckboxMessage(
              totalOriginalScore,
              awardedScore,
              isCorrectAnswer
            );
          } else {
            if (answerType !== 'textarea' && answerType !== 'none') {
              getFeedbackMessage(
                totalOriginalScore,
                awardedScore,
                isCorrectAnswer
              );
            }
          }
        }

        if (isManualScorring || !customFeedback) {
          updateTaskStatusToCompleted(player);
        }
      } else {
        if (answerType === 'checkbox') {
          if (
            getPlayerAttemptsLeft() <= 0 ||
            getCheckboxScore(data.forms![formIndex]) ===
              data!.forms![formIndex]?.score! ||
            (playerTasks?.taskContentFormAnswers![formIndex]
              .isScoringReduction &&
              getPlayerAttemptsLeft() <= 0)
          ) {
            if (!isManualScorring) {
              getCheckboxMessage(
                totalOriginalScore,
                awardedScore,
                isCorrectAnswer
              );
            }

            // go to next question will handled here if question doesnt have feedback
            // and manual scoring
            if (isManualScorring || !customFeedback) {
              setTimeout(nextQuestion, 500);
            }
          }
        } else {
          if (isCorrectAnswer || getPlayerAttemptsLeft() === -1) {
            if (answerType !== 'textarea' && answerType !== 'none') {
              if (!isManualScorring) {
                getFeedbackMessage(
                  totalOriginalScore,
                  awardedScore,
                  isCorrectAnswer
                );
              }
            }

            // go to next question will handled here if question doesnt have feedback
            // and manual scoring
            if (isManualScorring || !customFeedback) {
              setTimeout(nextQuestion, 500);
            }
          }
        }
      }

      // await deleteFromStore('upload-queue', `${props.id}-${formId}`);
    }
  };

  /**
   * Get checkbox message (feedback)
   * @param awardedScore
   */
  const getCheckboxMessage = (
    totalOriginalScore: number,
    awardedScore: number,
    isCorrectAnswer: boolean
  ) => {
    const newPlayerState = updatePlayerAnswerScore(player, awardedScore);
    if (newPlayerState) {
      // Update answer score selected to player state
      setPlayer((prev) => {
        return UpdatePlayerStateAsync(prev, newPlayerState);
      });
    }

    let customFeedback = GetCustomFeedback(
      gameDocument.gameDocument,
      data.id,
      formIndex
    );

    if (customFeedback) {
      // temporary note: "isCorrectAnswer" is a trigger to display a popup of the correct answer.
      if (Number.isFinite(totalOriginalScore) && isCorrectAnswer) {
        const playerAnswers = contentAnswer.filter((x) => x.value as boolean);

        const correctOptions = getQuestionCorrectAnswers().map(
          (x) => x.answerResId
        );

        const checkedCorrectOptions = playerAnswers.filter((x) =>
          correctOptions.includes(x.answerResId)
        ).length;

        const isPartialCorrect =
          correctOptions.length !== checkedCorrectOptions;

        const isDefault = isPartialCorrect
          ? customFeedback.isDefaultPartiallyCorrect
          : customFeedback.isDefaultAllCorrect;

        const url = isDefault
          ? imageUrl(
              isPartialCorrect
                ? 'defaultFeedbackPartialCorrectIconResId'
                : 'defaultFeedbackAllCorrectIconResId'
            )
          : GetResourceValue(
              gameDocument?.gameDocument!,
              isPartialCorrect
                ? customFeedback?.partiallyCorrectUrlResId!
                : customFeedback?.allCorrectUrlResId!,
              ''
            );

        const message = isDefault
          ? defaultMessage(
              isPartialCorrect
                ? 'defaultPartialCorrectFeedbackMessageResId'
                : 'defaultAllCorrectFeedbackMessageResId'
            )
          : GetResourceValue(
              gameDocument?.gameDocument!,
              isPartialCorrect
                ? customFeedback?.partiallyCorrectMessageResId!
                : customFeedback?.allCorrectMessageResId!,
              player?.playerState?.language?.name
            );

        showFeedbackToast(
          message,
          url,
          isPartialCorrect ? 'partial' : 'correct',
          awardedScore
        );
      } else {
        const isDefault = customFeedback.isDefaultAllWrong;

        const url = isDefault
          ? imageUrl('defaultFeedbackAllWrongIconResId')
          : GetResourceValue(
              gameDocument?.gameDocument!,
              customFeedback?.allWrongUrlResId!,
              ''
            );

        const message = isDefault
          ? defaultMessage('defaultAllWrongFeedbackMessageResId')
          : GetResourceValue(
              gameDocument?.gameDocument!,
              customFeedback?.allWrongMessageResId!,
              player?.playerState?.language?.name
            );

        showFeedbackToast(message, url, 'wrong');
      }
    }
  };

  const imageUrl = useCallback(
    (
      res:
        | 'defaultFeedbackAllCorrectIconResId'
        | 'defaultFeedbackPartialCorrectIconResId'
        | 'defaultFeedbackAllWrongIconResId'
    ) => {
      if (
        gameDocument.gameDocument?.settings &&
        gameDocument.gameDocument?.settings.designer
      ) {
        const feedbackIcon = GetResourceValue(
          gameDocument.gameDocument!,
          gameDocument.gameDocument?.settings.designer[res],
          ''
        );
        return feedbackIcon !== '' ? feedbackIcon : defaultImage[res];
      } else {
        return defaultImage[res];
      }
    },
    [gameDocument.gameDocument?.settings]
  );

  const defaultMessage = React.useCallback(
    (
      type:
        | 'defaultAllCorrectFeedbackMessageResId'
        | 'defaultAllWrongFeedbackMessageResId'
        | 'defaultPartialCorrectFeedbackMessageResId'
    ) => {
      if (
        gameDocument.gameDocument?.settings &&
        gameDocument.gameDocument?.settings.designer
      ) {
        const message = GetResourceValue(
          gameDocument.gameDocument,
          gameDocument.gameDocument.settings.designer[type],
          player.playerState?.language.name
        );

        const value =
          message == '' ||
          (type === 'defaultAllCorrectFeedbackMessageResId' &&
            message === 'All correct feedback message default') ||
          (type === 'defaultAllWrongFeedbackMessageResId' &&
            message === 'All wrong feedback message default') ||
          (type === 'defaultPartialCorrectFeedbackMessageResId' &&
            message === 'Well done, you are partially correct.')
            ? defaultFeedbackMessage[type]
            : message;
        return value;
      }

      return defaultFeedbackMessage[type];
    },
    [gameDocument.gameDocument?.settings]
  );

  /**
   * Get feedback message
   * @param awardedScore
   */
  const getFeedbackMessage = (
    totalOriginalScore: number,
    awardedScore: number,
    isCorrectAnswer: boolean
  ) => {
    let questionScore = data!.forms![formIndex]!.score! ?? 0;
    if (answerType === 'radio') {
      // * Function to add up the scores of all correct answers
      const scoreCorrectAnswer = getQuestionCorrectAnswers().map(
        (x) => x.score ?? 0
      );
      questionScore = CalculateTotal(scoreCorrectAnswer);
    }

    const newPlayerState = updatePlayerAnswerScore(player, awardedScore);
    if (newPlayerState) {
      // Update answer score selected to player state
      setPlayer((prev) => {
        return UpdatePlayerStateAsync(prev, newPlayerState);
      });
    }

    const customFeedback = GetCustomFeedback(
      gameDocument.gameDocument,
      data.id,
      formIndex
    );

    if (customFeedback) {
      // for answerType other than 'file', 'image' and 'video', then check the score
      // for answerType 'file', 'image' and 'video', then check isFileUploaded

      // temporary note: "isCorrectAnswer" is a trigger to display a popup of the correct answer.

      if (
        (Number.isFinite(totalOriginalScore) && isCorrectAnswer) ||
        answerType === 'file' ||
        answerType === 'image' ||
        answerType === 'video'
      ) {
        const isDefault = customFeedback.isDefaultAllCorrect;

        const url = isDefault
          ? imageUrl('defaultFeedbackAllCorrectIconResId')
          : GetResourceValue(
              gameDocument?.gameDocument!,
              customFeedback?.allCorrectUrlResId!,
              ''
            );

        const message = isDefault
          ? defaultMessage('defaultAllCorrectFeedbackMessageResId')
          : GetResourceValue(
              gameDocument?.gameDocument!,
              customFeedback?.allCorrectMessageResId!,
              player?.playerState?.language?.name
            );

        showFeedbackToast(message, url, 'correct', awardedScore);
      } else {
        const isDefault = customFeedback.isDefaultAllWrong;

        const url = isDefault
          ? imageUrl('defaultFeedbackAllWrongIconResId')
          : GetResourceValue(
              gameDocument?.gameDocument!,
              customFeedback?.allWrongUrlResId!,
              ''
            );

        const message = isDefault
          ? defaultMessage('defaultAllWrongFeedbackMessageResId')
          : GetResourceValue(
              gameDocument?.gameDocument!,
              customFeedback?.allWrongMessageResId!,
              player?.playerState?.language?.name
            );

        showFeedbackToast(message, url, 'wrong');
      }
    }
  };

  /**
   * Show chechbox answer toast
   * @param message
   * @param imageUrl
   */
  const showFeedbackToast = (
    message: string,
    imageUrl: string,
    status: 'correct' | 'wrong' | 'partial',
    score?: number
  ) => {
    const addItemNotification: AchievementContent = {
      isHide: false,
      imageUrl: imageUrl,
      priorityQueueNumber: 2,
      onCloseCallback: () => {
        const playerTasks = GetPlayerTaskById(player, props.id);
        if (data.forms && playerTasks) {
          data.forms.length === playerTasks.taskContentFormAnswers?.length
            ? updateTaskStatusToCompleted(player)
            : nextQuestion();
        }
      },
      message: (
        <>
          <div className={'d-flex justify-content-center flex-column h-100'}>
            <div
              className={
                'd-flex flex-column align-items-center justify-content-center mb-auto p-3'
              }>
              {score && (
                <span>{`${score} ${
                  score > 1
                    ? generateTitleById(
                        '4b8c7c2b-9d0f-5e60-ab2c-3d4e5f6a7b8c',
                        gameDocument,
                        displayLanguageContext.displayLanguageSelected
                          .resources!,
                        'game'
                      ) || 'Points'
                    : generateTitleById(
                        '3a7b6b1a-8c9e-4d5f-9a1b-2c3d4e5f6a7b',
                        gameDocument,
                        displayLanguageContext.displayLanguageSelected
                          .resources!,
                        'game'
                      ) || 'Point'
                }`}</span>
              )}
              <span className={'fw-bold'} style={{ fontSize: '20px' }}>
                {status === 'correct'
                  ? generateTitleById(
                      '5c9d8d3c-0e10-6f71-bc3d-4e5f6a7b8c9d',
                      gameDocument,
                      displayLanguageContext.displayLanguageSelected.resources!,
                      'game'
                    ) || 'Excellent!'
                  : status === 'partial'
                    ? generateTitleById(
                        '6a7b8c9d-0e1f-2a3b-4c5d-6e7f8a9b0c1d',
                        gameDocument,
                        displayLanguageContext.displayLanguageSelected
                          .resources!,
                        'game'
                      ) || 'Partially correct'
                    : generateTitleById(
                        '6d0e9e4d-1f11-7082-cd4e-5f6a7b8c9d0e',
                        gameDocument,
                        displayLanguageContext.displayLanguageSelected
                          .resources!,
                        'game'
                      ) || 'Unlucky.'}
              </span>
            </div>
            <div className={'d-flex justify-content-center mb-auto p-3'}>
              {message}
            </div>
          </div>
        </>
      )
    };
    const content: AchievementContent[] = toast.content;
    content.push(addItemNotification);
    setToast({ ...toast, content });
  };

  const checkCorrectAnswer = async (): Promise<boolean> => {
    let isAnswerCorrect = true;

    if (validateScoring()) {
      if (answerType === 'text') {
        //Check default answer has been set
        let acceptableAnswersData: string = '';
        const taskAnswer = data!.forms![formIndex];
        if (taskAnswer && taskAnswer.answers) {
          acceptableAnswersData = getResourceById(
            taskAnswer.answers[0].answerResId
          );
        }

        //compare player answer with acceptable answer
        if (!isEmpty(acceptableAnswersData)) {
          // check is acceptable answer is using wildcard or not
          const isWildcard = (answer: string) =>
            answer.startsWith('.*') && answer.endsWith('.*');

          const answer = contentAnswer[0]?.value;
          // split multiple answer using |
          const patternMap = acceptableAnswersData
            .split('|')
            .map((acceptableAnswer) => {
              // if using wildcard, return regex instance
              if (isWildcard(acceptableAnswer)) {
                // remove the .* from start and end to get the actual pattern
                const pattern = acceptableAnswer.slice(2, -2);
                // create regex that matches the pattern anywhere in the string
                return new RegExp(pattern, 'i');
              }
              return acceptableAnswer;
            });

          // Try to match the answer with acceptable answer using
          isAnswerCorrect = patternMap.some((pattern) =>
            pattern instanceof RegExp
              ? pattern.test(answer || '')
              : pattern === answer
          );
        } else {
          //Task has no default answer, set it correct by default
          isAnswerCorrect = true;
        }
      } else if (
        answerType === 'textarea' ||
        answerType === 'date' ||
        answerType === 'time'
      ) {
        //Check default answer has been set
        let acceptableAnswers: string = '';
        const taskAnswer = data!.forms![formIndex];
        if (taskAnswer && taskAnswer.answers) {
          acceptableAnswers = getResourceById(
            taskAnswer.answers[0].answerResId
          );
        }

        //compare player answer with acceptable answer
        if (!isEmpty(acceptableAnswers)) {
          isAnswerCorrect =
            contentAnswer.length > 0 &&
            contentAnswer[0]?.value?.toLowerCase() ===
              acceptableAnswers?.toLowerCase();
        } else {
          //Task has no default answer, set it correct by default
          isAnswerCorrect = true;
        }
      } else if (answerType === 'number') {
        // Get the form
        const form = data!.forms![formIndex];

        if (form && form.answers && form.answers.length >= 2) {
          // Check the answer range
          const minRange = getResourceById(form!.answers[0].answerResId);
          const maxRange = getResourceById(form!.answers[1].answerResId);
          const requiresRange = minRange !== '' && maxRange !== '';

          if (requiresRange && form.answers && form.answers.length >= 2) {
            // Check if minRange and maxRange are equal
            if (minRange === maxRange) {
              const playerAnswer = parseInt(
                contentAnswer[0]?.value?.toString()
              );
              isAnswerCorrect = playerAnswer === parseInt(minRange);
            }

            // Validate answer within range
            if (
              minRange !== '' &&
              maxRange !== '' &&
              contentAnswer.length > 0
            ) {
              const playerAnswer = parseInt(
                contentAnswer[0]?.value?.toString()
              );
              isAnswerCorrect =
                playerAnswer >= parseInt(minRange) &&
                playerAnswer <= parseInt(maxRange);
            }
          }

          if (!requiresRange) {
            // Validate it should be greater than minRange
            if (minRange) {
              isAnswerCorrect =
                parseInt(contentAnswer[0]?.value?.toString()) >=
                parseInt(minRange);
            }

            // Validate it should be less than maxRange
            if (maxRange) {
              isAnswerCorrect =
                parseInt(contentAnswer[0]?.value?.toString()) <=
                parseInt(maxRange);
            }
          }
        } else {
          //Task has no default answer, set it correct by default
          isAnswerCorrect = true;
        }
      } else if (answerType === 'checkbox') {
        if (validateScoring()) {
          isAnswerCorrect = checkboxAnswerCheckHandler(data.forms![0]!);
        }
      } else if (answerType === 'radio') {
        const correctAnswer = data.forms![formIndex].answers?.find(
          (x) => x.isChecked
        );
        const playerAnswer = contentAnswer.find((x) => x.value as boolean);
        isAnswerCorrect =
          contentAnswer.length > 0 &&
          correctAnswer?.answerResId === playerAnswer?.answerResId;
      } else if (answerType === 'image' || answerType === 'video') {
        isAnswerCorrect = contentAnswer.length > 0;
      }
    }

    const task = GetPlayerTaskById(player, props.id);

    if (task) {
      const form = cloneDeep(
        task.taskContentFormAnswers?.find(
          (x) => x?.formId === data.forms![formIndex].id
        )
      );
      if (!isAnswerCorrect) {
        if (form && form.score !== undefined) {
          form.score =
            form.score - (data.forms![formIndex]?.pointDepreciation ?? 0);
        }
        // #6529 If attempsLeft is finished and the answer is still wrong, the score will immediately be changed to 0
        if (form && form.attemptsLeft && form.attemptsLeft - 1 <= 0) {
          form.score = 0;
        }
        if (form && form.attemptsLeft && form.attemptsLeft > 0) {
          form.attemptsLeft = form.attemptsLeft - 1;
        }
      }

      if (form && form.isCorrectAnswer !== undefined) {
        form.isCorrectAnswer = isAnswerCorrect;
      }

      task.taskContentFormAnswers![formIndex!] = form!;

      setPlayer((prev) => {
        const response = UpdateTaskAsync(prev, props.id, task);
        return UpdatePlayerStateAsync(prev, response);
      });
    }

    return isAnswerCorrect;
  };

  const getPlayerAttemptsLeft = (): number => {
    if (formIndex < 0) return -1;
    if (player.playerState && data.forms && props.id) {
      return (
        GetPlayerTaskById(player, props.id)?.taskContentFormAnswers?.find(
          (x) => x?.formId === data.forms![formIndex]?.id
        )?.attemptsLeft ?? -1
      );
    }
    return -1;
  };

  const getPlayerAnswerStatus = (): boolean | undefined => {
    if (formIndex < 0) return;
    if (player.playerState && data.forms && props.id) {
      return (
        GetPlayerTaskById(player, props.id)?.taskContentFormAnswers?.find(
          (x) => x?.formId === data.forms![formIndex]?.id
        )?.isCorrectAnswer ?? undefined
      );
    }
    return undefined;
  };

  const getPlayerScore = (): number => {
    if (formIndex < 0) return -1;
    if (player.playerState && data.forms && props.id) {
      if (answerType !== 'checkbox' && answerType !== 'radio') {
        return (
          GetPlayerTaskById(player, props.id)?.taskContentFormAnswers?.find(
            (x) => x?.formId === data.forms![formIndex]?.id
          )?.score ?? 0
        );
      } else {
        // #6695
        //new behaviour calculate player score when answer type is checkbox or radio
        let playerAnswers = contentAnswer.filter((x) => x.value as boolean);

        const score =
          answerType === 'radio'
            ? calculateAnswerWithScore(playerAnswers)
            : getCheckboxScore(data.forms[formIndex]);

        return score;
      }
    }
    return 0;
  };

  /**
   * Get answer score of question
   * @param form
   * @returns
   */
  const getAnswerScore = (form: TaskContentForm) => {
    if (form) {
      if (form.type === 'checkbox') {
        return getCheckboxScore(form);
      } else if (form.type === 'radio') {
        let playerAnswers = contentAnswer.filter((x) => x.value as boolean);
        return calculateAnswerWithScore(playerAnswers);
      }

      return form.score ?? 0;
    }
  };

  /**
   * Calculate Player Score for answer type checkbox and radio
   */
  const calculateAnswerWithScore = (playerAnswers: ContentAnswer[]) => {
    const playerContentAnswers = data.forms![formIndex].answers?.filter(
      (answer) =>
        playerAnswers.some((i) => i.answerResId === answer.answerResId)
    );

    const calculateScore = playerContentAnswers?.reduce((total, item) => {
      const score =
        item?.score && typeof item.score === 'number' ? item.score : 0;
      return total + score;
    }, 0);
    return calculateScore ?? 0;
  };

  /**
   * Get & calculate checkbox score
   * @returns
   */
  const getCheckboxScore = (form: TaskContentForm) => {
    if (form) {
      let playerAnswers = contentAnswer.filter((x) => x.value as boolean);
      let score = calculateAnswerWithScore(playerAnswers);

      return score;
    }
    return 0;
  };

  /**
   * Get correct checkbox answer
   * @returns boolean
   */
  const checkboxAnswerCheckHandler = (form: TaskContentForm) => {
    if (form) {
      const playerAnswers = contentAnswer.filter((x) => x.value as boolean);

      const correctOptions = getQuestionCorrectAnswers().map(
        (x) => x.answerResId
      );

      const wrongOptions = form.answers
        ?.filter((i) => !i.isChecked)
        ?.map((i) => i.answerResId);

      const checkedCorrectOptions = playerAnswers.filter((x) =>
        correctOptions.includes(x.answerResId)
      ).length;

      const checkedWrongOptions = wrongOptions
        ? playerAnswers.filter((x) => wrongOptions.includes(x.answerResId))
            .length
        : 0;

      return (
        (checkedWrongOptions === 0 || !checkedWrongOptions) &&
        checkedCorrectOptions !== 0
      );
    }
    return false;
  };

  /**
   * Validate the question is able to score or not
   * @returns
   */
  const validateScoring = () => {
    const playerAttemptsLeft = getPlayerAttemptsLeft();

    // Task has manual scoring, no need to check for acceptable answers
    const isManualScorring = data!.forms![formIndex].isManualScoring;
    return (
      data &&
      data.forms &&
      formIndex > -1 &&
      playerAttemptsLeft !== 0 &&
      !isManualScorring
    );
  };

  /**
   * Get correct answers of question
   * @returns
   */
  const getQuestionCorrectAnswers = () => {
    if (validateScoring()) {
      return data.forms![formIndex].answers?.filter((x) => x.isChecked) ?? [];
    }
    return [];
  };

  const updateAnswerToPlayerState = async () => {
    if (player.playerState && data.forms) {
      const formId = data.forms[formIndex]?.id;
      let task = GetPlayerTaskById(player, props.id);

      if (task) {
        let isExistAnswer = task?.taskContentFormAnswers?.find(
          (x) => x?.formId === formId
        );
        const isScoringReduction = data.forms![formIndex].isScoringReduction;

        const createNewFormAnswer = (): TaskContentAnswerEntity => ({
          formId: formId,
          answers: cloneDeep(contentAnswer),
          attemptsLeft: data.forms![formIndex]?.maxAttempts ?? -1,
          isCorrectAnswer: true,
          score: getAnswerScore(data.forms![formIndex])
        });

        const addNewFormAnswer = () => {
          if (!task!.taskContentFormAnswers) {
            task!.taskContentFormAnswers = [];
          }
          task!.taskContentFormAnswers.push(createNewFormAnswer());
        };

        if (isExistAnswer && !isExistAnswer.isScoringReduction) {
          task.taskContentFormAnswers![formIndex].answers =
            cloneDeep(contentAnswer);
        } else if (!isExistAnswer) {
          addNewFormAnswer();
        } else if (isScoringReduction && isExistAnswer) {
          const formAnswer = task.taskContentFormAnswers![formIndex];
          formAnswer.answers = cloneDeep(contentAnswer);
          formAnswer.isCorrectAnswer = true;
          formAnswer.attemptsLeft =
            formAnswer.attemptsLeft ?? data.forms[formIndex].maxAttempts ?? -1;
          formAnswer.score = getAnswerScore(data.forms[formIndex]);
        }

        return UpdateTaskAsync(player, props.id, task);
      }
    }
  };

  //update task answer player state
  const updatePlayerAnswerScore = (
    playerContext: PlayerContextState,
    awardedScore: number
  ) => {
    if (playerContext && data.forms) {
      const formId = data.forms[formIndex]?.id;
      const task = GetPlayerTaskById(playerContext, props.id);
      let taskContentAnswer = task?.taskContentFormAnswers?.find(
        (i) => i.formId === formId
      );
      if (taskContentAnswer) {
        taskContentAnswer.score = awardedScore;
      }
      return UpdateTaskAsync(playerContext, props.id, task!);
    }
  };

  const onCheckboxChangeHandler = React.useCallback(
    (index: number, value: boolean) => {
      const form = cloneDeep(data?.forms![formIndex]);
      if (form) {
        const answerResId = form.answers
          ? form.answers[index]?.answerResId
          : '';
        let newContentAnswer: ContentAnswer[] = cloneDeep(contentAnswer);

        if (answerResId !== '') {
          const answerIndex = newContentAnswer?.findIndex(
            (x) => x.answerResId === answerResId
          );

          if (answerIndex > -1) {
            newContentAnswer[answerIndex].value = value;
            newContentAnswer[answerIndex].score = form.answers
              ? form.answers[answerIndex]?.score
              : 0;
          }

          if (answerIndex === -1) {
            const score = form.answers ? form.answers[answerIndex]?.score : 0;

            newContentAnswer.push({
              answerResId: answerResId,
              value: value,
              score
            });
          }

          if (value) {
            setTotalTryAnswer(totalTryAnswer + 1);
          } else {
            setTotalTryAnswer(totalTryAnswer - 1);
            //if player uncheck answer, remove answer from content answer
            newContentAnswer.splice(answerIndex, 1);
          }

          setContentAnswer(cloneDeep(newContentAnswer));
        }
      }
    },
    [contentAnswer]
  );

  //On input change (single input like Text, Textarea, Date, Number)
  const onSingleInputChangeAnswer = (
    value: string | number | File,
    size?: number
  ) => {
    if (!data.forms) return;

    if (typeof value === 'string' || typeof value === 'number') {
      updateAnswerValue(value as string | number, size);
    } else {
      const taskId = props.id;
      const form = data.forms[formIndex];
      const formId = form.id;
      const isManualScoring = form.isManualScoring ?? false;

      updateAnswerValue('');
      handleFileChange(value, taskId, formId, isManualScoring)
        .then((res) => {
          if (res) {
            updateAnswerValue(res.url, res.contextSize);
          }
        })
        .catch((err) => {
          console.error('Failed to handle file change:', err);
        });
    }
  };

  const updateAnswerValue = (
    value: string | number | undefined,
    size?: number
  ) => {
    if (contentAnswer.length === 0) {
      let newAnswer: ContentAnswer[] = [];
      const form = cloneDeep(data?.forms![formIndex]);
      if (
        form &&
        !['video', 'image', 'file', 'number'].includes(answerType ?? '')
      ) {
        form?.answers?.forEach((answer) => {
          newAnswer.push({
            answerResId: answer?.answerResId,
            value: value,
            size: size,
            score: answer?.score
          });
        });
      }

      if (['video', 'image', 'file'].includes(answerType ?? '')) {
        newAnswer.push({ value, size, answerResId: '' });
      }

      if (form && form.answers && ['number'].includes(answerType ?? '')) {
        newAnswer.push({
          answerResId: form.answers[0].answerResId,
          value: value,
          size: size,
          score: form.answers[0].score
        });
      }

      setContentAnswer(cloneDeep(newAnswer));
    } else {
      let newAnswer: ContentAnswer[] = cloneDeep(contentAnswer);
      newAnswer[0].value = value;
      newAnswer[0].size = size;
      setContentAnswer(cloneDeep(newAnswer));
    }
  };

  //On radio button index change
  const onRadioChangeAnswer = (value: number) => {
    let newAnswer: ContentAnswer[] = [];
    const form = cloneDeep(data?.forms![formIndex]);
    if (form) {
      form?.answers?.forEach((answer, index) => {
        newAnswer.push({
          answerResId: answer?.answerResId,
          value: (index === value) as boolean,
          score: answer?.score
        });
      });
      setContentAnswer(cloneDeep(newAnswer));
    }
  };

  const checkTaskPlayerStateIsExist = async () => {
    if (player.playerState) {
      const taskPlayer = GetPlayerTaskById(player, props.id);
      //If not exist then add
      if (!taskPlayer) {
        let newTask: TaskEntityPlayer = {
          id: props.id,
          isVisible: true,
          taskContentId: props.id,
          taskContentFormAnswers: []
        };

        setPlayer((prev) => {
          const response = AddTask(prev, newTask);
          return UpdatePlayerStateAsync(prev, response);
        });
      }
    }
  };

  const repopulateQuestion = () => {
    getType();
    getQuestion();
    const forms = cloneDeep(data.forms);
    if (forms && forms[formIndex]) {
      const form = cloneDeep(forms[formIndex]);
      getOptions(form.answers ?? []);
      setSelectedCheckboxOptions(cloneDeep(form.answers ?? []));
      setAnswer(form.id);
    }
  };

  const setSelectedCheckboxOptions = (answers: TaskContentAnswer[]) => {
    if (answerType === 'checkbox') {
      if (formIndex > -1) {
        let option: CheckboxOption[] = [];
        if (answers) {
          answers?.forEach((answer) => {
            const description = getResourceById(answer.answerResId);
            option.push({
              answerResId: answer.answerResId,
              description: description ?? '',
              isChecked:
                (contentAnswer?.find(
                  (x) => x.answerResId === answer?.answerResId
                )?.value as boolean) ?? false
            });
          });

          setCheckboxOption(cloneDeep(option));
        }
      }
    }
  };

  const renderPlayerAttempts = (): JSX.Element => {
    const attemptsLeft: number = getPlayerAttemptsLeft();
    if (data?.forms) {
      const maxAttempts: number =
        formIndex > -1 ? (data?.forms![formIndex]?.maxAttempts ?? -1) : -1;

      if (attemptsLeft > 0 && attemptsLeft < maxAttempts) {
        return (
          <span>
            {(
              generateTitleById(
                '298f372e-f97b-42c6-b1a7-a0bf7866ec7b',
                gameDocument,
                displayLanguageContext.displayLanguageSelected.resources!,
                'game'
              ) || 'Try again - % attempt(s) remaining'
            ).replace('%', attemptsLeft.toString())}
          </span>
        );
      }
    }
    return <></>;
  };

  const isDisabledAnswer = () => {
    const isTimeRemaining = isQuestionWithTimer
      ? (questionScoringReduction?.maximumTimeLimit ?? 0) < 1
      : false;

    return (
      getPlayerAttemptsLeft() === 0 ||
      getPlayerAnswerStatus() === true ||
      (getPlayerAttemptsLeft() === -1 && getPlayerAnswerStatus() === false) ||
      isTimeRemaining
    );
  };

  const resetTaskContent = async () => {
    setFormIndex(-1);
    setQuestion('');
    setOptions([]);
    setAnswerType('none');
    // setTaskQuestion({content: "", embeddedBlobs: []});
    setContentAnswer([]);
    setCheckboxOption([]);
    setTotalTryAnswer(0);
    // setQuestionScoringReduction({});
    // setCounterTimeInterval(1);
  };

  const generatePopupMessage = (answerTypeProps?: AnswerType): string => {
    switch (answerTypeProps) {
      case 'image':
        return (
          generateTitleById(
            '42b77164-cdf8-4222-94ad-4e5652b6b2f8',
            gameDocument,
            displayLanguageContext.displayLanguageSelected.resources!
          ) || 'No photo submitted. Please try again.'
        );
      case 'video':
        return (
          generateTitleById(
            '4981eed4-5e4a-4f90-819e-385c7634a48e',
            gameDocument,
            displayLanguageContext.displayLanguageSelected.resources!
          ) || 'No video submitted. Please try again.'
        );
      case 'file':
        return 'No file submitted. Please try again.';
      default:
        return 'No answer submitted. Please try again.';
    }
  };

  const getScoringReduction = async () => {
    if (player.playerState && data.forms) {
      const formId = data.forms[formIndex]?.id;
      let task = GetPlayerTaskById(player, props.id);
      if (task) {
        const newPlayerState = await setPlayerScoreReduction(
          player,
          props.id,
          data.forms,
          formIndex,
          task,
          formId
        );
        if (newPlayerState) {
          setPlayer((prev) => UpdatePlayerStateAsync(prev, newPlayerState));
          let isExistAnswer = task?.taskContentFormAnswers?.find(
            (x: TaskContentAnswerEntity) => x?.formId === formId
          );

          if (isExistAnswer) {
            const currentTaskContentFormAnswers =
              task?.taskContentFormAnswers![formIndex];
            setQuestionScoringReduction(
              currentTaskContentFormAnswers.scoringReductions
            );
          }
        }
      }
    }
  };

  // hidden close, previous, and next button if
  // question with timer
  // timer still running
  // and not answered yet
  const isUnableToCloseOrChangeForm = useMemo(() => {
    const isAnsweredQuestion = getPlayerAnswerStatus();
    if (
      formIndex > -1 &&
      isAnsweredQuestion === undefined &&
      data.forms &&
      questionScoringReduction?.maximumTimeLimit &&
      questionScoringReduction.maximumTimeLimit > 0
    ) {
      const currentQuestionForm = data.forms[formIndex];
      return currentQuestionForm.isScoringReduction;
    }

    return false;
  }, [formIndex, data.forms, questionScoringReduction]);

  const taskProximityValue = React.useMemo(() => {
    let proximityValue = game.gameState?.runtime?.inGame.setTaskProximity;
    const selectedTask = GetTaskGameById(gameDocument.gameDocument, props.id);

    if (selectedTask && selectedTask.settingsOverride)
      proximityValue = (
        selectedTask.settingsOverride.setTaskProximity ?? 0
      ).toString();

    return proximityValue;
  }, [
    props.id,
    gameDocument.gameDocument,
    game.gameState?.runtime?.inGame.setTaskProximity
  ]);

  // Move the getTaskContent function to run first
  useEffect(() => {
    resetTaskContent();
  }, [props.id]);

  useEffect(() => {
    if (formIndex == -1) {
      getTaskContent(props.id);
      hasMounted.current = true;
    }

    (async function fetchData() {
      if (formIndex > -1) {
        if (data?.forms && data?.forms[formIndex]?.isScoringReduction) {
          await getScoringReduction();
        }
        repopulateQuestion();
      }
    })();

    setTotalTryAnswer(0);
  }, [formIndex]);

  useEffect(() => {
    if (formIndex > -1 && data && data.forms) {
      setSelectedCheckboxOptions(
        cloneDeep(data.forms[formIndex]?.answers ?? [])
      );
    }
    // eslint-disable-next-line
  }, [contentAnswer]);

  useEffect(() => {
    const task = GetPlayerTaskById(player, props.id);

    if (task?.isForceComplete && task?.status !== 'completed') {
      updateTaskStatusToCompleted(player);
    }

    if (task?.status === 'incomplete' && !task.isForceComplete) {
      props.changeTaskIconToInComplete(task?.id!);
    }
  }, [player.playerState, props.id]);

  useEffect(() => {
    const playerCode = player.playerState?.code ?? '';
    if (!playerCode) return;
    if (formIndex === -1) return;

    (async () => {
      const uploads = await getFromStore<TFileUpload>(
        'upload-queue',
        playerCode
      );
      if (!uploads) return;
      if (uploads.files.length === 0) return;

      const taskId = props.id;
      const formId = data.forms![formIndex].id;
      const selectedUploads = uploads.files.find(
        (f) => f.taskId === taskId && f.formId === formId
      );
      if (selectedUploads)
        setContentAnswer([
          {
            value: '',
            size: 0,
            answerResId: ''
          }
        ]);
    })();
  }, [player.playerState?.code, props.id, data.forms, formIndex]);

  return (
    <>
      {isPreMessage ? (
        <div className={`achievement-dialog__mask premessage`}>
          <div className={`achievement-dialog`}>
            <div className={`achievement-dialog__body`}>
              {isLoadingContent ? (
                <div className={`d-flex`} style={{ marginTop: 'unset' }}>
                  <Loader size="large" type="infinite-spinner" />
                </div>
              ) : (
                <>
                  {!props.isAccesible && (
                    <div className="alert alert-warning" role="alert">
                      You must be in the range
                      {` ${taskProximityValue ?? 0} meters`}
                    </div>
                  )}

                  {data.bodyType === 'HTML' ? (
                    <RichTextContent
                      key={props.id}
                      content={preMessageContent?.content ?? ''}
                      embeddedBlobs={preMessageContent?.embeddedBlobs}
                    />
                  ) : (
                    <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                      {preMessageContent?.content ?? ''}
                    </ReactMarkdown>
                  )}
                </>
              )}
            </div>
            <div className={'achievement-dialog__footer d-flex gap-2'}>
              <Button
                id={'btn-task-content-premessage-close'}
                themeColor={'secondary'}
                fillMode={'outline'}
                onClick={() => onCloseTaskContentHandler(false)}>
                {generateTitleById(
                  '48bc820b-40e8-4624-8d46-b68e16013be6',
                  gameDocument,
                  displayLanguageContext.displayLanguageSelected.resources!,
                  'game'
                ) || 'Close'}
              </Button>
              {props.isAccesible &&
                !GetTaskGameById(gameDocument.gameDocument, props.id)
                  ?.enableQRCode && (
                  <Button
                    id={'btn-task-content-premessage-continue'}
                    themeColor={'success'}
                    fillMode={'solid'}
                    onClick={onLaunchHandler}
                    disabled={isDisabledAnswer()}>
                    {generateTitleById(
                      'be998642-6834-40d7-b457-55acafbe0fc0',
                      gameDocument,
                      displayLanguageContext.displayLanguageSelected.resources!,
                      'game'
                    ) || 'Continue'}
                  </Button>
                )}
            </div>
          </div>
        </div>
      ) : isTaskIntro || (data && data.forms && data.forms.length > 0) ? (
        <div className={'task-content__mask'} key={formIndex}>
          <TimerProgressForm
            formIndex={formIndex}
            taskContentId={data.id}
            onSubmitHandler={onSubmitHandler}
          />

          <Container
            id={'task-content-container'}
            className={'task-content px-0'}
            fluid>
            <div className={'content__header'}>
              <h1 className={'fw-bold p-0 m-0'}>{title}</h1>
              <div
                className={`${isUnableToCloseOrChangeForm ? 'd-none' : ''} d-flex justify-content-center align-items-center cursor-pointer`}
                onClick={() => onCloseTaskContentHandler()}>
                <span className={'material-symbols-outlined fw-bold'}>
                  close
                </span>
              </div>
            </div>

            <div className={'d-flex gap-4 border-bottom px-3 pt-3'}>
              {[
                ...(isTaskIntro ? ['Task information'] : []),
                ...(data && data.forms && data.forms.length > 0
                  ? ['Questions']
                  : [])
              ].map((tab) => (
                <div
                  key={tab}
                  style={{
                    cursor: 'pointer',
                    ...(selectedTab === tab && {
                      color: '#003A64',
                      fontWeight: 'bold',
                      paddingBottom: '5px',
                      padding: '0 5px 8px',
                      borderBottom: '3px solid #003A64'
                    })
                  }}
                  onClick={() => {
                    setSelectedTab(tab);
                    if (tab === 'Questions' && formIndex < 0) setFormIndex(0);
                  }}>
                  {tab}
                </div>
              ))}
            </div>

            {selectedTab === 'Task information' && (
              <>
                <div className={'content__body'}>
                  <div className={'content__intro'}>
                    {isLoadingContent ? (
                      <div
                        className={`d-flex w-100 justify-content-center`}
                        style={{ marginTop: 'unset' }}>
                        <Loader size="large" type="infinite-spinner" />
                      </div>
                    ) : (
                      <>
                        {data.bodyType === 'HTML' ? (
                          <RichTextContent
                            key={props.id}
                            content={taskQuestion?.content ?? ''}
                            embeddedBlobs={taskQuestion?.embeddedBlobs}
                          />
                        ) : (
                          <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                            {taskQuestion?.content ?? ''}
                          </ReactMarkdown>
                        )}
                      </>
                    )}
                  </div>
                </div>

                <div className={'content__footer'}>
                  <div className={'content'}>
                    <div className="d-flex align-items-center justify-content-end">
                      <Button
                        id={'btn-task-content-submit'}
                        themeColor={'success'}
                        onClick={nextQuestion}>
                        <div className={'d-flex'}>
                          <span>
                            {generateTitleById(
                              'be998642-6834-40d7-b457-55acafbe0fc0',
                              gameDocument,
                              displayLanguageContext.displayLanguageSelected
                                .resources!,
                              'game'
                            ) || 'Continue'}
                          </span>
                        </div>
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            )}

            {selectedTab === 'Questions' && (
              <>
                <div className={'content__body'}>
                  <div className={`d-flex justify-content-center`}>
                    <div
                      className={`d-flex flex-column align-items-center ${['text', 'number', 'time'].includes(answerType ?? '') ? 'short-answer' : 'long-answer'}`}>
                      {(data.forms ?? []).length > 1 && (
                        <h2 className={'fw-bold'}>
                          {formIndex + 1}/{(data.forms ?? []).length}
                        </h2>
                      )}

                      <h2 className={'fw-bold'}>{question}</h2>

                      {data.forms &&
                        formIndex > -1 &&
                        GetFormMedia(
                          gameDocument?.gameDocument!,
                          data.forms[formIndex]
                        ).length > 0 && (
                          <div className={'d-flex justify-content-center mb-1'}>
                            <Gallery
                              className={'task-content-media'}
                              items={GetFormMedia(
                                gameDocument?.gameDocument!,
                                data.forms[formIndex]
                              )}
                            />
                          </div>
                        )}
                    </div>
                  </div>

                  <div
                    id={'task-content-answer'}
                    className={`d-flex flex-column flex-grow-1 ${
                      isDisabledAnswer() ? 'disabled-answer' : ''
                    }`}>
                    <Answer
                      key={formIndex}
                      answerInput={
                        answerType === 'number'
                          ? (contentAnswer[0]?.value ?? 0)
                          : (contentAnswer[0]?.value ?? '')
                      }
                      answerOptionIndex={
                        contentAnswer?.findIndex(
                          (item: ContentAnswer) => item.value as boolean
                        ) ?? -1
                      }
                      answerType={answerType}
                      checkboxOption={checkboxOption}
                      formId={
                        data.forms && formIndex > -1
                          ? data.forms[formIndex]?.id
                          : ''
                      }
                      isCorrectAnswer={getPlayerAnswerStatus()}
                      isManualScoring={
                        data!.forms && formIndex > -1
                          ? data!.forms![formIndex].isManualScoring
                          : false
                      }
                      name={data?.name}
                      onChangeAnswerInput={onSingleInputChangeAnswer}
                      onChangeOptionIndex={onRadioChangeAnswer}
                      onCheckboxChange={onCheckboxChangeHandler}
                      options={options}
                      taskId={props.id}
                      isDisabled={isDisabledAnswer()}
                      isUploading={isUploading}
                      isOnline={isOnline}
                      uploadStatus={
                        uploadStatus[
                          `${props.id}_${data?.forms && formIndex > -1 ? data.forms[formIndex]?.id : ''}`
                        ]
                      }
                      uploadError={
                        uploadErrors[
                          `${props.id}_${data?.forms && formIndex > -1 ? data.forms[formIndex]?.id : ''}`
                        ]
                      }
                      onRetry={() => {
                        const currentFId =
                          data?.forms && formIndex > -1
                            ? data.forms[formIndex]?.id
                            : '';
                        if (currentFId) retryUpload(props.id, currentFId);
                      }}
                      onCancel={() => {
                        const currentFId =
                          data?.forms && formIndex > -1
                            ? data.forms[formIndex]?.id
                            : '';
                        if (currentFId) cancelUpload(props.id, currentFId);
                      }}
                    />
                  </div>
                </div>

                <div className={'content__footer'}>
                  <div className={'content'}>
                    <div
                      className={
                        'align-items-center d-flex justify-content-between'
                      }>
                      <div className={`flex-grow-1`}>
                        {formIndex > 0 && (
                          <Button
                            onClick={() => setFormIndex((prev) => prev - 1)}
                            disabled={isUploading}
                            fillMode={'flat'}
                            className={`fw-bold ${isUnableToCloseOrChangeForm ? 'd-none' : ''}`}>
                            <span className={'d-flex align-items-center'}>
                              <span className={'material-symbols-outlined'}>
                                navigate_before
                              </span>
                              <span>
                                {generateTitleById(
                                  '714c5007-ce05-477e-8dc2-8f9502eb4c45',
                                  gameDocument,
                                  displayLanguageContext.displayLanguageSelected
                                    .resources!,
                                  'game'
                                ) || 'Previous'}
                              </span>
                            </span>
                          </Button>
                        )}
                      </div>

                      <div className="d-flex justify-items-between gap-2">
                        {renderPlayerAttempts()}
                      </div>

                      <div className="d-flex align-items-center justify-content-end">
                        {formIndex !== (data.forms ?? []).length - 1 && (
                          <Button
                            fillMode={'flat'}
                            onClick={nextQuestion}
                            disabled={isUploading}
                            className={`mr-2 fw-bold ${isUnableToCloseOrChangeForm ? 'd-none' : ''}`}>
                            <span className={'d-flex align-items-center'}>
                              <span>
                                {generateTitleById(
                                  '079c039a-8207-4404-8eb6-558cf5d8477b',
                                  gameDocument,
                                  displayLanguageContext.displayLanguageSelected
                                    .resources!,
                                  'game'
                                ) || 'Next'}
                              </span>
                            </span>
                          </Button>
                        )}

                        <Button
                          id={'btn-task-content-submit'}
                          themeColor={'success'}
                          onClick={() => {
                            const formId = data.forms![formIndex].id;

                            // if not timer question but answer still empty, show warning
                            if (
                              !isQuestionWithTimer &&
                              contentAnswer.length === 0 &&
                              answerType !== 'none'
                            ) {
                              setShowNoAsnwerWarning(true);
                              return;
                            }

                            onSubmitHandler();
                          }}
                          disabled={isDisabledAnswer() || isUploading}>
                          <div className={'d-flex'}>
                            <span>{`${
                              formIndex > -1 &&
                              getPlayerAttemptsLeft() > -1 &&
                              getPlayerAttemptsLeft() <
                                data.forms![formIndex].maxAttempts!
                                ? generateTitleById(
                                    'c0acec61-3782-4e51-bac5-3cfa930c2586',
                                    gameDocument,
                                    displayLanguageContext
                                      .displayLanguageSelected.resources!,
                                    'game'
                                  ) || 'Re-submit'
                                : generateTitleById(
                                    '8065dbef-51c3-4e7b-82e8-48ea0bfdf093',
                                    gameDocument,
                                    displayLanguageContext
                                      .displayLanguageSelected.resources!,
                                    'game'
                                  ) || 'Submit'
                            }`}</span>
                          </div>
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </Container>

          {showNoAsnwerWarning && (
            <ConfirmationDialog
              title=" "
              icon={'warning'}
              widthDialog={500}
              message={generatePopupMessage(answerType)}
              cancelText={
                generateTitleById(
                  '48bc820b-40e8-4624-8d46-b68e16013be6',
                  gameDocument,
                  displayLanguageContext.displayLanguageSelected.resources!,
                  'game'
                ) || 'Close'
              }
              onCancelCallback={() => setShowNoAsnwerWarning(false)}
            />
          )}
        </div>
      ) : null}
      {showARWarning && (
        <ConfirmationDialog
          title={'Unsupported Device'}
          icon={'warning'}
          widthDialog={500}
          message={
            'This task cannot be opened on your current device. Please use an iOS device to access this task.'
          }
          cancelText={
            generateTitleById(
              '48bc820b-40e8-4624-8d46-b68e16013be6',
              gameDocument,
              displayLanguageContext.displayLanguageSelected.resources!,
              'game'
            ) || 'Close'
          }
          onCancelCallback={() => {
            onCloseTaskContentHandler();
            setShowARWarning();
          }}
        />
      )}
    </>
  );
};
